import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AssessmentLogin() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Auto-verify if magic_token is in the URL
  useEffect(() => {
    const token = searchParams.get("magic_token");
    if (token) {
      verifyToken(token);
    }
  }, [searchParams]);

  // Check if already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API}/jump-assessment/auth/me`, { credentials: "include" });
        if (res.ok) {
          navigate("/assessments/dashboard", { replace: true });
        }
      } catch {}
    };
    if (!searchParams.get("magic_token")) checkAuth();
  }, []);

  const verifyToken = async (token) => {
    setVerifying(true);
    try {
      const res = await fetch(`${API}/jump-assessment/auth/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Login successful!");
        navigate("/assessments/dashboard", { replace: true });
      } else {
        toast.error(data.detail || "Invalid or expired link");
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/jump-assessment/auth/request-magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok || res.status === 200) {
        setSent(true);
      } else {
        toast.error(data.detail || "Failed to send link");
      }
    } catch {
      toast.error("Failed to send link");
    } finally {
      setSending(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#070B14] flex items-center justify-center" data-testid="assessment-login-verifying">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14] flex items-center justify-center px-4" data-testid="assessment-login-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black tracking-wider text-white">
            <span className="text-blue-500">PALADIN</span> ASSESSMENTS
          </h1>
          <p className="text-slate-500 text-sm mt-2 tracking-widest uppercase">Coach Portal</p>
        </div>

        <div className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-8">
          {sent ? (
            /* Success State */
            <div className="text-center space-y-4" data-testid="magic-link-sent">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Check Your Email</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                If <span className="text-white font-medium">{email}</span> has been approved for access,
                a login link has been sent. Click the link to sign in.
              </p>
              <p className="text-slate-500 text-xs">The link expires in 15 minutes.</p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium mt-4 transition-colors"
                data-testid="try-different-email"
              >
                Try a different email
              </button>
            </div>
          ) : (
            /* Login Form */
            <form onSubmit={requestLink} className="space-y-5">
              <div className="text-center mb-2">
                <h2 className="text-lg font-semibold text-white">Sign In</h2>
                <p className="text-slate-500 text-sm mt-1">Enter your email to receive a login link</p>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoFocus
                  className="w-full bg-[#141B2D] border border-[#1E293B] rounded-xl px-4 py-3 text-white text-sm
                    focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 outline-none transition-all
                    placeholder:text-slate-600"
                  data-testid="assessment-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400
                  text-white font-semibold py-3 rounded-xl transition-colors text-sm"
                data-testid="request-magic-link-btn"
              >
                {sending ? "Sending..." : "Send Login Link"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          Only coaches approved by the admin can access this portal.
        </p>
      </div>
    </div>
  );
}
